import React, {useCallback, useEffect, useState} from 'react';
import {useMediaQuery} from 'react-responsive';
import {useAppDispatch} from 'store/customer';
import {
    pageRestrictedSet,
    restrictionMessageDismissedSet,
} from 'store/customer/appSlice';
import styled from 'styled-components';

interface MobileRestrictionProps {
    children: React.ReactNode;
    message: string;
    onlyShowMessage?: boolean;
}

export const MobileRestriction = ({
    children,
    message,
    onlyShowMessage = true,
}: MobileRestrictionProps) => {
    const dispatch = useAppDispatch();
    const isMobilePortrait = useMediaQuery({
        query: '(orientation: portrait) and (max-width: 820px)',
    });
    const isMobileLandscape = useMediaQuery({
        query: '(orientation: landscape) and (max-width: 1180px)',
    });
    const isBetween820And1180 = useMediaQuery({
        query: '(min-width: 820px) and (max-width: 1180px)',
    });
    const isMobile = isMobilePortrait || isMobileLandscape;
    const [dismissed, setDismissed] = useState(false);
    const handleClose = useCallback(() => {
        setDismissed(true);
    }, []);
    const isRestricted = isMobile || (!isMobile && isBetween820And1180);

    useEffect(() => {
        dispatch(pageRestrictedSet(isRestricted));
        dispatch(restrictionMessageDismissedSet(dismissed));
    }, [isRestricted, dismissed]);

    // Render children in a stable slot so the returned element tree keeps the
    // same shape (a Fragment with the same two children) whether or not the
    // screen is restricted. Switching between a bare `children` element and a
    // wrapping Fragment made React unmount/remount the subtree whenever an
    // orientation change flipped `isRestricted`, which in turn wiped transient
    // state (e.g. the room planner modal selection) via component unmount.
    const shouldRenderChildren = !isRestricted || !onlyShowMessage;
    return (
        <>
            {isRestricted && !dismissed ? (
                <Message
                    $noStyle={onlyShowMessage}
                    className="restriction-alert-message">
                    {message}
                    {!onlyShowMessage ? (
                        <span onClick={handleClose} data-cy="close-message">
                            X
                        </span>
                    ) : null}
                </Message>
            ) : null}
            {shouldRenderChildren ? children : null}
        </>
    );
};

const Message = styled.strong<{$noStyle: boolean}>`
    ${({$noStyle, theme}) => {
        if (!$noStyle) {
            return `
                display: block;
                margin-top: 15px;
                padding: 5px 25px 5px 5px;
                border-radius: 8px;
                margin-right: 60px;
                background: ${theme.colors.secondary.main};
                color: white;
                position: relative;
                font-size: 1rem;

                @media (max-width: 650px) {
                    font-size: 0.65rem;
                    padding: 5px 25px 5px 10px;
                    margin-left: 5px;
                }

                > span {
                    position: absolute;
                    top: 5px;
                    right: 5px;
                    width: 18px;
                    height: 18px;
                    font-weight: 100;
                    border-radius: 18px;
                    border: 1px solid white;
                    line-height: 1;
                    text-align: center;
                    font-size: .9em;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            `;
        }
    }}
`;
